import React, { useEffect, useState, useCallback } from "react";
import SpotLightSearch from "../SpotLight/SpotLight";
import NotificationDropDown from "../NotificationDropDown/NotificationDropDown";
import Search from "../Search/Search";
import CustomButton from "../Button/CustomButton";
import { USER_ROLE } from "../../../Constants/constant";
import { roleAccess } from "../../../helpers/RolesPermission";
import { PERMISSION_KEYS } from "store/constants";
import { classNames } from "Utils/utils";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_TEAM_LEAD_GET_MANAGERS_REGIONS_LIST,
  URL_TEAM_LEAD_GET_REGION_AND_MANAGERS,
  URL_TEAM_LEAD_MANAGER_REGION_STATUS_UPDATE,
  URL_TEAM_LEAD_SP_REGION_STATUS_UPDATE,

} from "store/services/URL";
import CustomMultiSelect from "../CustomMulitDropdown";

const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const Header = ({
  title,
  desc = "",
  breadcrumb = null,
  isButton = false,
  isButtonDisabled = false,
  handleClick,
  btnLabel = "",
  icon,
  btnCss,
  variants,
  btnComponent = null,
  btnComponentBeforeRegions = null,
  titleBadge = null,
  cssClass,
  isSticky = true,
}) => {
  const [searchBox, setSearchBox] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [loader, setLoader] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);

  const checkScreenSize = () => {
    setIsMediumScreen(window.innerWidth < 1300);
  };

  useEffect(() => {
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);
  const fetchRegionsList = async () => {
    setLoading(true);
  
    await apiGetRequest(URL_TEAM_LEAD_GET_MANAGERS_REGIONS_LIST)
      .then((res) => {
        let regionsList = [
          { label: "All", value: "all" },
          ...res?.data?.data?.map((el) => ({
            label: el?.name,
            value: el?.id,
          })),
        ];
        setRegions(regionsList);
        const isStatusChanged = res?.data?.data
          ?.filter((el) => {
            if (el?.pivot) {
              return el?.pivot?.status;
            } else {
              return el?.status;
            }
          })
          ?.map((el) => ({ label: el?.name, value: el?.id })) ?? [];

        // Preserve "All" selection if user had selected it before (e.g. after reload)
        const savedRegionsStr = localStorage.getItem("selectedRegions");
        const hadAllSelected =
          savedRegionsStr &&
          (() => {
            try {
              const parsed = JSON.parse(savedRegionsStr);
              return Array.isArray(parsed) && parsed.some((item) => item?.value === "all");
            } catch {
              return false;
            }
          })();

        const regionsToStore = hadAllSelected ? [{ label: "All", value: "all" }] : isStatusChanged;
        localStorage.setItem("selectedRegions", JSON.stringify(regionsToStore));
        setSelectedRegions(regionsToStore);
        setLoading(false);
      })
      .catch((error) => {
        console.log({ error });
        setLoading(false);
      });
  };

  const handleApiCall = async (values = [], previousValues = []) => {
    setLoader(true);
    let payload = {};
    let regionsIds = [];

    if (values?.some((el) => el?.value === "all")) {
      payload = {
        region_id: "all",
      };
    } else {
      regionsIds = values?.map((el) => el.value);
      payload = {
        region_id: regionsIds,
      };
    }
    const URL = roleAccess([USER_ROLE?.Sp_manager]) ? URL_TEAM_LEAD_MANAGER_REGION_STATUS_UPDATE : URL_TEAM_LEAD_SP_REGION_STATUS_UPDATE;

    try {
      const res = await apiPostRequest(URL, payload);
      if (res.data) {
        localStorage.setItem("selectedRegions", JSON.stringify(values));
        // Page reload is necessary to refresh all components that depend on region settings
        // Use a small delay to ensure state is updated before reload
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    } catch (error) {
      console.log(error);
      // Revert selection on error - restore previous state
      setSelectedRegions(previousValues);
      localStorage.setItem("selectedRegions", JSON.stringify(previousValues));
    } finally {
      setLoader(false);
    }
  };

  // Reduced debounce from 1500ms to 500ms for better responsiveness
  const debouncedApiCall = useCallback(debounce(handleApiCall, 500), []);

  const handleChange = (e) => {
    // Store previous value for error recovery
    const previousValues = selectedRegions;
    
    // Optimistically update UI immediately for smooth, responsive UX
    setSelectedRegions(e);
    // Save to localStorage immediately for persistence and to prevent flickering
    // This will be overwritten by the API response on success, or reverted on error
    localStorage.setItem("selectedRegions", JSON.stringify(e));
    // Debounce the API call to avoid excessive requests
    // Pass previous values for error recovery
    debouncedApiCall(e, previousValues);
  };

  useEffect(() => {
    const savedRegions = localStorage.getItem("selectedRegions");

    if (savedRegions) {
      setSelectedRegions(JSON.parse(savedRegions));
    }
  }, []);

  useEffect(() => {
    if (roleAccess([USER_ROLE.Sp_manager, USER_ROLE.Sp_Admin])) {
      fetchRegionsList();
    }
  }, []);

  return (
    <header
      className={classNames(
        "bg-white border-b border-gray-200 px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-5",
        isSticky && "sticky top-0 z-40",
        cssClass && `${cssClass}`
      )}
    >
      <div className='w-full'>
        {/* Top Row: Title and Actions */}
        <div className='flex flex-col sm:flex-row sm:flex-wrap sm:items-start sm:justify-between gap-3 sm:gap-4'>
          {/* Title Section — min width prevents one-character-per-line collapse when zoom &gt; 100% */}
          <div className='flex-1 min-w-0'>
            {breadcrumb && (
              <div className='mb-2 min-w-0 overflow-x-auto'>{breadcrumb}</div>
            )}
            <div className='flex flex-wrap items-center gap-2 min-w-0'>
              <h1 className='text-lg sm:text-xl font-semibold text-gray-900 capitalize truncate'>
                {title}
              </h1>
              {titleBadge}
            </div>
            {desc && (
              <p className='mt-1 max-w-full break-words text-xs sm:text-sm text-gray-500'>
                {desc}
              </p>
            )}
          </div>

          {/* Right Side Actions - Always visible but responsive */}
          <div className='flex w-full min-w-0 flex-shrink-0 flex-col sm:w-auto sm:flex-row sm:flex-wrap items-start sm:items-center justify-start sm:justify-end gap-3'>
            {/* Content before Selected Regions (e.g. Add New Document) */}
            {btnComponentBeforeRegions && <>{btnComponentBeforeRegions}</>}
            {/* Region Selector - On the same row as buttons on larger screens */}
            {roleAccess([USER_ROLE.Sp_manager, USER_ROLE.Sp_Admin]) && (
              <div className='flex flex-col sm:flex-row gap-2 sm:items-center sm:gap-2 w-full sm:w-auto'>
                <label className='block text-xs sm:text-sm text-gray-500 whitespace-nowrap'>
                  Selected Regions
                </label>
                <div className='w-full sm:w-auto sm:min-w-[150px]'>
                  <CustomMultiSelect
                    options={regions}
                    selectedRegions={selectedRegions}
                    setSelectedRegions={setSelectedRegions}
                    onFocusHandler={fetchRegionsList}
                    onChange={handleChange}
                    isDisabled={loader}
                    isManagerModule
                    isApiLoading={loading}
                  />
                </div>
              </div>
            )}

            {/* Buttons and Icons Section */}
            <div className='flex items-center gap-2 sm:gap-3 flex-wrap'>
              {/* Buttons Section */}
              {btnComponent && <>{btnComponent}</>}
              {isButton && (
                <CustomButton
                  clickHandler={handleClick}
                  label={btnLabel}
                  Icon={icon}
                  variant={variants}
                  cssClass={btnCss}
                  isDisabled={isButtonDisabled}
                />
              )}

              {/* Search and Notification Icons */}
              {roleAccess([
                USER_ROLE.Sp_manager,
                USER_ROLE.Sp_Admin,
                USER_ROLE.Sp_Standard_User,
              ]) && (
                <div className='flex items-center gap-2 sm:gap-3'>
                  {roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <div className='flex-shrink-0'>
                      <Search
                        cssClass='w-5 h-5'
                        isSearchOnly={true}
                        handleClick={() => setSearchBox(true)}
                      />
                    </div>
                  )}
                  <div className='flex-shrink-0'>
                    <NotificationDropDown />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {roleAccess([], PERMISSION_KEYS.PARTICIPANT) && searchBox && (
        <SpotLightSearch handleClose={() => setSearchBox(false)} />
      )}
    </header>
  );
};

export default Header;
